import { CHARACTER_MODERATION_STATUS, useKindeFeatureFlags, useTranslation } from "@next-platform/client-common-core";
import { IconVariants, Input, Size, useMedia } from "@next-platform/client-common-spicy-ui";
import type { TFunction } from "i18next";
import type { ReactNode } from "react";

import { CreationPaths } from "@/types/common";

import Dropdown from "./common/Dropdown";
import { FilterKeys, FilterOptions, IOptions, SortOptions } from "./type";

const INPUT_WIDTH = 400;

// Visibility-only filter options (All / Public / Private / Unlisted), without the
// moderation-status entries. Exported so callers can opt out of Under Review /
// Action Required (e.g. the Attach Lorebook modal).
// eslint-disable-next-line id-length -- keep "t" as the standard i18n translation helper for readability and tooling support
export const getVisibilityFilterOptions = (t: TFunction, contentType?: CreationPaths): IOptions[] => [
  { label: t("myCreation:filter.all"), value: "All", key: FilterKeys.ALL },
  { label: t("myCreation:filter.public"), value: "public", key: FilterKeys.VISIBILITY },
  { label: t("myCreation:filter.private"), value: "private", key: FilterKeys.VISIBILITY },
  ...(contentType !== CreationPaths.LOREBOOKS ? [{ label: t("myCreation:filter.unlisted"), value: "hidden", key: FilterKeys.VISIBILITY }] : []),
];

// eslint-disable-next-line id-length -- keep "t" as the standard i18n translation helper for readability and tooling support
const getBotListToolbarOptions = (t: TFunction, contentType?: CreationPaths) => {
  if (contentType === CreationPaths.LOREBOOKS) {
    return {
      defaultSortOptions: [
        { label: t("myCreation:sort.latest"), value: SortOptions.LATEST },
        { label: t("myCreation:sort.topRated"), value: SortOptions.TOP_RATED },
      ],
      filterOptions: [
        ...getVisibilityFilterOptions(t, contentType),
        { label: t("myCreation:filter.underReview"), value: CHARACTER_MODERATION_STATUS.PENDING, key: FilterKeys.MODERATION_REPORT_STATUS },
        { label: t("myCreation:filter.actionRequired"), value: CHARACTER_MODERATION_STATUS.FIX_REQUIRED, key: FilterKeys.MODERATION_REPORT_STATUS },
      ],
    };
  }

  return {
    defaultSortOptions: [
      { label: t("myCreation:sort.latest"), value: SortOptions.LATEST },
      { label: t("myCreation:sort.topRated"), value: SortOptions.TOP_RATED },
    ],
    filterOptions: [
      ...getVisibilityFilterOptions(t),
      { label: t("myCreation:filter.underReview"), value: CHARACTER_MODERATION_STATUS.PENDING, key: FilterKeys.MODERATION_STATUS },
      { label: t("myCreation:filter.actionRequired"), value: CHARACTER_MODERATION_STATUS.FIX_REQUIRED, key: FilterKeys.MODERATION_STATUS },
    ],
  };
};

// eslint-disable-next-line id-length -- keep "t" as the standard i18n translation helper for readability and tooling support
const getTranslatedLegacySortLabel = (value: string, label: string, t: TFunction) => {
  if (label !== value) {
    return label;
  }

  switch (value) {
    case SortOptions.LATEST:
      return t("myCreation:sort.latest");
    case SortOptions.TOP_RATED:
      return t("myCreation:sort.topRated");
    case SortOptions.OLDEST:
      return t("myCreation:sort.oldest");
    case SortOptions.NAME:
      return t("myCreation:sort.name");
    default:
      return label;
  }
};

const createItemsProps = (items: IOptions[], callback: (option: IOptions) => void) =>
  items.map((option: IOptions) => ({
    label: option.label,
    value: option.value,
    onPress: () => callback(option),
  }));

const getOptionLabel = (value: string, options: IOptions[]) => {
  const option = options.find((currentOption: IOptions) => currentOption.value === value);
  return option ? option.label : null;
};

interface IBotListToolbarV2 {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  filterBy: FilterOptions;
  onFilterChange?: (filterKey: FilterKeys, filterValue?: FilterOptions) => void;
  sortBy: string;
  onSortChange?: (value: SortOptions) => void;
  hideFilter?: boolean;
  filterOptions?: IOptions[];
  sortOptions?: IOptions[];
  hideSort?: boolean;
  /**
   * When provided, the search input renders full-width on its own row and the
   * filter/sort dropdowns move to a second row aligned to the right of this node
   * (e.g. a "12 results found" label).
   */
  resultsLabel?: ReactNode;
  contentType?: CreationPaths;
  searchPlaceholder?: string;
}

const BotListToolbarV2 = ({
  searchQuery,
  onSearchChange,
  filterBy,
  onFilterChange,
  sortBy,
  onSortChange,
  hideFilter,
  hideSort,
  filterOptions,
  sortOptions,
  resultsLabel,
  contentType,
  searchPlaceholder,
}: IBotListToolbarV2) => {
  const { t } = useTranslation();
  const { hasAccessToV2 } = useKindeFeatureFlags();
  const { isMobile } = useMedia();
  const { defaultSortOptions, filterOptions: defaultFilterOptions } = getBotListToolbarOptions(t, contentType);
  const resolvedFilterOptions = filterOptions ?? defaultFilterOptions;
  const resolvedSortOptions = (sortOptions ?? defaultSortOptions).map((sortOption: IOptions) => ({
    ...sortOption,
    label: getTranslatedLegacySortLabel(sortOption.value, sortOption.label, t),
  }));
  const filterItemsProps = createItemsProps(resolvedFilterOptions, (filterItem: IOptions) =>
    onFilterChange?.(filterItem.key as FilterKeys, filterItem.value as FilterOptions),
  );

  const sortItemsProps = createItemsProps(resolvedSortOptions, (sortItem: IOptions) => onSortChange?.(sortItem.value as SortOptions));

  const dropdowns =
    !hideFilter || !hideSort ? (
      <div className="flex gap-md justify-end" data-testid="BotListToolbarV2-Dropdowns">
        {!hideFilter && (
          <Dropdown
            currentValue={getOptionLabel(filterBy, filterItemsProps)}
            hasAccessToV2={hasAccessToV2}
            listItems={filterItemsProps}
            iconEnd={IconVariants.CHEVRON_DOWN}
          />
        )}
        {!hideSort && (
          <Dropdown
            currentValue={getOptionLabel(sortBy, sortItemsProps)}
            hasAccessToV2={hasAccessToV2}
            listItems={sortItemsProps}
            iconEnd={IconVariants.CHEVRON_DOWN}
          />
        )}
      </div>
    ) : null;

  const searchInput = (
    <Input
      data-testid="BotListToolbarV2-SearchInput"
      value={searchQuery}
      iconStart={IconVariants.SEARCH}
      isClearable={!!searchQuery}
      iconEnd={searchQuery ? IconVariants.CLOSE : undefined}
      placeholder={searchPlaceholder ?? t("common:search")}
      onChange={onSearchChange}
      size={Size.MEDIUM}
      width={isMobile || (hideFilter && hideSort) || resultsLabel != null ? "100%" : INPUT_WIDTH}
    />
  );

  // When a resultsLabel is provided, stack the full-width search above a row
  // that pairs the label (left) with the filter/sort dropdowns (right).
  if (resultsLabel != null) {
    return (
      <div className="flex flex-col gap-md" data-testid="BotListToolbarV2-WithResultsLabel">
        {searchInput}
        <div className="flex gap-md justify-between items-center max-mob:flex-col max-mob:items-stretch">
          <div>{resultsLabel}</div>
          {dropdowns}
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-md justify-between max-mob:flex-col">
      {searchInput}
      {dropdowns}
    </div>
  );
};

export default BotListToolbarV2;
